import React, { useState, useEffect } from 'react';
import tripService from '../../services/tripService';
import communicationService, { COMM_EVENT_KEYS } from '../../services/communicationService';
import { useVehicles } from '../../hooks/useVehicles';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';

const todayIso = () => new Date().toISOString().split('T')[0];

export default function TripForm({ trip, onSuccess, onCancel }) {
  const { vehicles, selectedVehicle, handleDriverScopeError } = useVehicles();
  const { user } = useAuth();
  const isDriver = user?.role === 'driver';
  const isEdit = Boolean(trip);

  const [form, setForm] = useState({
    vehicle: selectedVehicle?.id || '',
    driver: user?.role === 'driver' ? user.id : '',
    date: todayIso(),
    route: '', origin: '', destination: '',
    declared_amount: '',
    notes: '',
  });
  const [sendEmail, setSendEmail] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (trip) setForm(p => ({ ...p, ...trip, vehicle: trip.vehicle?.id || trip.vehicle, driver: trip.driver?.id || trip.driver }));
  }, [trip]);

  const handle = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (isDriver) payload.date = todayIso();

      const saved = isEdit
        ? await tripService.update(trip.id, payload)
        : await tripService.create(payload);

      if (sendEmail) {
        try {
          await communicationService.dispatchEvent(COMM_EVENT_KEYS.TRIP_RECORDED, {
            trip_id: saved?.id || trip?.id,
            vehicle_id: form.vehicle,
            driver_id: form.driver,
            context: { action: isEdit ? 'trip_updated' : 'trip_recorded' },
          });
        } catch {
          toast.error('Trip saved, but email dispatch is not configured');
        }
      }

      toast.success(isEdit ? 'Trip updated' : 'Trip recorded');
      onSuccess();
    } catch (err) {
      if (isDriver && handleDriverScopeError(err)) {
        return;
      }
      const d = err.response?.data;
      if (d) Object.values(d).forEach(v => toast.error(Array.isArray(v) ? v[0] : v));
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit}>
      {!isDriver && (
        <div className="form-group">
          <label className="form-label">Vehicle *</label>
          <select className="form-control" name="vehicle" value={form.vehicle} onChange={handle} required>
            <option value="">Select vehicle</option>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.registration_number} — {v.make} {v.model}</option>)}
          </select>
        </div>
      )}
      <div className="grid-2">
        <div className="form-group">
          <label className="form-label">Date *</label>
          <input
            className="form-control"
            type="date"
            name="date"
            value={isDriver ? todayIso() : form.date}
            onChange={handle}
            required
            disabled={isDriver}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Route</label>
          <input className="form-control" name="route" value={form.route} onChange={handle} placeholder="CBD — Thika Road" />
        </div>
      </div>
      <div className="grid-2">
        <div className="form-group">
          <label className="form-label">Origin</label>
          <input className="form-control" name="origin" value={form.origin} onChange={handle} placeholder="Nairobi CBD" />
        </div>
        <div className="form-group">
          <label className="form-label">Destination</label>
          <input className="form-control" name="destination" value={form.destination} onChange={handle} placeholder="Thika Town" />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Total Fare Collected (KES) *</label>
        <input className="form-control" type="number" name="declared_amount" value={form.declared_amount} onChange={handle} placeholder="10000" required min="0" style={{ fontFamily: 'var(--font-mono)' }} />
        <span className="form-hint">Total amount collected from all passengers for this trip cycle</span>
      </div>
      <div className="form-group">
        <label className="form-label">Notes</label>
        <textarea className="form-control" name="notes" value={form.notes} onChange={handle} rows={2} placeholder="Any remarks…" />
      </div>
      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
          />
          Send trip update email
        </label>
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving…' : isEdit ? 'Update Trip' : 'Record Trip'}
        </button>
      </div>
    </form>
  );
}